import { useState, useRef, useEffect, ReactNode, Fragment } from 'react';
import { GripVertical } from 'lucide-react';
import '@/styles/resizable-panels.css';

interface Panel {
  id: string;
  minWidth?: number;
  maxWidth?: number;
  defaultWidth: number;
  children: ReactNode;
}

interface ResizablePanelsProps {
  panels: Panel[];
  onPanelResize?: (panelWidths: Record<string, number>) => void;
  storageKey?: string;
  /**
   * Optional: which panel should flex to fill remaining space.
   * If omitted, defaults to the last panel.
   */
  flexPanelId?: string;
  /**
   * Optional: which panel should be active by default.
   * If omitted, defaults to the first panel.
   */
  defaultActivePanel?: string;
}

export function ResizablePanels({ panels, onPanelResize, storageKey = 'panel-widths', flexPanelId, defaultActivePanel }: ResizablePanelsProps) {
  const [panelWidths, setPanelWidths] = useState<Record<string, number>>(() => {
    // Use default widths initially
    const widths: Record<string, number> = {};
    
    // Calculate total available width
    const viewportWidth = typeof window !== 'undefined' ? window.innerWidth : 1920;
    const totalDefaultWidth = panels.reduce((sum, p) => sum + p.defaultWidth, 0);
    const handleWidths = (panels.length - 1) * 1; // 1px per handle
    
    // If defaults exceed viewport, scale them down proportionally
    if (totalDefaultWidth + handleWidths > viewportWidth) {
      const scale = (viewportWidth - handleWidths - 50) / totalDefaultWidth; // -50 for safety margin
      panels.forEach(panel => {
        widths[panel.id] = Math.floor(panel.defaultWidth * scale);
      });
    } else {
      panels.forEach(panel => {
        widths[panel.id] = panel.defaultWidth;
      });
    }
    
    // Apply defaultActivePanel logic - give more space to the active panel
    if (defaultActivePanel) {
      const activePanel = panels.find(p => p.id === defaultActivePanel);
      if (activePanel) {
        // Increase the active panel width by 20% and reduce others proportionally
        const activeIndex = panels.findIndex(p => p.id === defaultActivePanel);
        const activeWidth = widths[activePanel.id];
        const increase = Math.floor(activeWidth * 0.2);
        
        // Reduce other panels proportionally
        const otherPanels = panels.filter((_, i) => i !== activeIndex);
        const otherTotal = otherPanels.reduce((sum, p) => sum + widths[p.id], 0);
        const reductionRatio = (otherTotal - increase) / otherTotal;
        
        otherPanels.forEach(panel => {
          widths[panel.id] = Math.floor(widths[panel.id] * reductionRatio);
        });
        
        // Increase active panel
        widths[activePanel.id] = activeWidth + increase;
      }
    }
    
    // Try to load saved widths from localStorage
    if (typeof window !== 'undefined' && storageKey) {
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        try {
          const savedWidths = JSON.parse(saved);
          // Validate saved widths fit in current viewport
          const savedTotal = Object.values(savedWidths).reduce((sum: number, w: any) => sum + w, 0);
          if (savedTotal + handleWidths <= viewportWidth) {
            return savedWidths;
          }
        } catch (e) {
          console.error('Failed to parse saved panel widths:', e);
        }
      }
    }
    
    return widths;
  });

  const [isResizing, setIsResizing] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const startXRef = useRef(0);
  const startWidthsRef = useRef<Record<string, number>>({});
  const defaultWidthsRef = useRef<Record<string, number>>({});
  if (Object.keys(defaultWidthsRef.current).length === 0) {
    const defaults: Record<string, number> = {};
    panels.forEach(p => { defaults[p.id] = p.defaultWidth; });
    defaultWidthsRef.current = defaults;
  }

  // Save widths to localStorage when they change
  useEffect(() => {
    if (storageKey && typeof window !== 'undefined') {
      localStorage.setItem(storageKey, JSON.stringify(panelWidths));
    }
    onPanelResize?.(panelWidths);
  }, [panelWidths, storageKey, onPanelResize]);

  const handleMouseDown = (e: React.MouseEvent, panelId: string) => {
    e.preventDefault();
    setIsResizing(panelId);
    startXRef.current = e.clientX;
    startWidthsRef.current = { ...panelWidths };
    
    // Add cursor style to body
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  };

  const handleTouchStart = (e: React.TouchEvent, panelId: string) => {
    const touch = e.touches[0];
    if (!touch) return;
    setIsResizing(panelId);
    startXRef.current = touch.clientX;
    startWidthsRef.current = { ...panelWidths };
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  };

  useEffect(() => {
    const applyResizeDelta = (deltaX: number) => {
      if (!isResizing || !containerRef.current) return;

      const panelIndex = panels.findIndex(p => p.id === isResizing);
      
      if (panelIndex === -1 || panelIndex === panels.length - 1) return;

      const currentPanel = panels[panelIndex];
      const nextPanel = panels[panelIndex + 1];
      const nextIsFlex = (nextPanel.id === (flexPanelId || panels[panels.length - 1]?.id));
      const currentIsFlex = (currentPanel.id === (flexPanelId || panels[panels.length - 1]?.id));
      
      const currentStartWidth = startWidthsRef.current[currentPanel.id];
      const nextStartWidth = startWidthsRef.current[nextPanel.id];
      
      let newCurrentWidth = currentStartWidth + deltaX;
      let newNextWidth = nextStartWidth - deltaX;
      
      // Apply min/max constraints
      const currentMin = currentPanel.minWidth || 200;
      const currentMax = currentPanel.maxWidth || 99999;
      const nextMin = nextPanel.minWidth || 200;
      const nextMax = nextPanel.maxWidth || 99999;
      
      // Get container width to ensure panels don't exceed viewport
      const containerWidth = containerRef.current?.offsetWidth || window.innerWidth;
      const otherPanelsWidth = panels.reduce((sum, p, i) => {
        if (i !== panelIndex && i !== panelIndex + 1) {
          return sum + (panelWidths[p.id] || p.defaultWidth);
        }
        return sum;
      }, 0);
      
      const maxAvailableWidth = containerWidth - otherPanelsWidth - (panels.length - 1); // handles

      if (nextIsFlex && !currentIsFlex) {
        // Only clamp current; leave at least nextMin for the flex panel
        const maxCurrent = Math.min(currentMax, maxAvailableWidth - nextMin);
        newCurrentWidth = Math.max(currentMin, Math.min(maxCurrent, newCurrentWidth));
        setPanelWidths(prev => ({
          ...prev,
          [currentPanel.id]: newCurrentWidth
        }));
        return;
      }

      if (currentIsFlex && !nextIsFlex) {
        // Only clamp next; leave at least currentMin for the flex panel
        const maxNext = Math.min(nextMax, maxAvailableWidth - currentMin);
        newNextWidth = Math.max(nextMin, Math.min(maxNext, newNextWidth));
        setPanelWidths(prev => ({
          ...prev,
          [nextPanel.id]: newNextWidth
        }));
        return;
      }

      // Neither side is flex → preserve pair total reasonably inside container
      newCurrentWidth = Math.max(currentMin, Math.min(currentMax, newCurrentWidth));
      newNextWidth = Math.max(nextMin, Math.min(nextMax, newNextWidth));

      const totalNewWidth = newCurrentWidth + newNextWidth;
      if (totalNewWidth > maxAvailableWidth) {
        const ratio = maxAvailableWidth / totalNewWidth;
        newCurrentWidth = Math.floor(newCurrentWidth * ratio);
        newNextWidth = Math.floor(newNextWidth * ratio);
      }

      setPanelWidths(prev => ({
        ...prev,
        [currentPanel.id]: newCurrentWidth,
        [nextPanel.id]: newNextWidth
      }));
    };

    const handleMouseMove = (e: MouseEvent) => {
      const deltaX = e.clientX - startXRef.current;
      applyResizeDelta(deltaX);
    };

    const handleMouseUp = () => {
      setIsResizing(null);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    const handleTouchMove = (e: TouchEvent) => {
      const touch = e.touches[0];
      if (!touch) return;
      const deltaX = touch.clientX - startXRef.current;
      applyResizeDelta(deltaX);
    };

    const handleTouchEnd = () => {
      setIsResizing(null);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.addEventListener('touchmove', handleTouchMove, { passive: false });
      document.addEventListener('touchend', handleTouchEnd);
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.removeEventListener('touchmove', handleTouchMove as any);
        document.removeEventListener('touchend', handleTouchEnd);
      };
    }
  }, [isResizing, panels]);

  // Re-clamp widths on window resize to prevent overflow and keep layout natural
  useEffect(() => {
    const onResize = () => {
      if (!containerRef.current) return;
      const containerWidth = containerRef.current.offsetWidth;
      const handleWidths = (panels.length - 1) * 1;
      const available = Math.max(0, containerWidth - handleWidths);

      const currentTotal = panels.reduce((sum, p) => sum + (panelWidths[p.id] ?? p.defaultWidth), 0);
      if (currentTotal <= available) {
        // Let the flex panel absorb extra space via flex-grow; keep widths
        return;
      }

      const scale = available > 0 && currentTotal > 0 ? available / currentTotal : 1;
      const next: Record<string, number> = {};
      panels.forEach(p => {
        const min = p.minWidth || 200;
        const max = p.maxWidth || 99999;
        const base = panelWidths[p.id] ?? p.defaultWidth;
        const scaled = Math.floor(base * scale);
        next[p.id] = Math.max(min, Math.min(max, scaled));
      });
      setPanelWidths(next);
    };

    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [panelWidths, panels]);

  const handleDoubleClickReset = (panelId: string) => {
    // Reset the pair split to their defaults proportionally
    const index = panels.findIndex(p => p.id === panelId);
    if (index === -1 || index === panels.length - 1) return;
    const current = panels[index];
    const next = panels[index + 1];
    const currentDefault = defaultWidthsRef.current[current.id] ?? current.defaultWidth;
    const nextDefault = defaultWidthsRef.current[next.id] ?? next.defaultWidth;

    const containerWidth = containerRef.current?.offsetWidth || window.innerWidth;
    const otherWidth = panels.reduce((sum, p, i) => {
      if (i !== index && i !== index + 1) {
        return sum + (panelWidths[p.id] || p.defaultWidth);
      }
      return sum;
    }, 0);
    const available = containerWidth - otherWidth - (panels.length - 1);
    const totalDefaults = currentDefault + nextDefault;
    if (available <= 0 || totalDefaults <= 0) return;
    const currentWidth = Math.floor((currentDefault / totalDefaults) * available);
    const nextWidth = Math.max(0, available - currentWidth);

    setPanelWidths(prev => ({
      ...prev,
      [current.id]: Math.max(current.minWidth || 200, Math.min(current.maxWidth || 99999, currentWidth)),
      [next.id]: Math.max(next.minWidth || 200, Math.min(next.maxWidth || 99999, nextWidth))
    }));
  };

  const effectiveFlexId = flexPanelId || (panels[panels.length - 1]?.id);

  return (
    <div ref={containerRef} className={`resizable-panels-container flex h-full w-full overflow-hidden ${isResizing ? 'is-resizing' : ''}`}>
      {panels.map((panel, index) => (
        <Fragment key={panel.id}>
          {/* Panel Content */}
          <div
            className={`resizable-panel h-full overflow-auto ${panel.id === (flexPanelId || panels[panels.length - 1]?.id) ? 'flex-1' : ''}`}
            style={panel.id === (flexPanelId || panels[panels.length - 1]?.id) ? {
              minWidth: `${panel.minWidth || 200}px`
            } : {
              width: `${panelWidths[panel.id]}px`,
              minWidth: `${panel.minWidth || 200}px`,
              maxWidth: `${panel.maxWidth || 600}px`,
              flexShrink: 0
            }}
          >
            {panel.children}
          </div>
          
          {/* Resize Handle (not for last panel) */}
          {index < panels.length - 1 && (
            <div
              className={`h-full bg-gray-200 cursor-col-resize hover:bg-gray-300 transition-colors flex-shrink-0 ${isResizing === panel.id ? 'bg-blue-400' : ''}`}
              style={{ width: '1px', minWidth: '1px', maxWidth: '1px' }}
              onMouseDown={(e) => handleMouseDown(e, panel.id)}
              onTouchStart={(e) => handleTouchStart(e, panel.id)}
              onDoubleClick={() => handleDoubleClickReset(panel.id)}
            />
          )}
        </Fragment>
      ))}
    </div>
  );
}

// Simpler version with just two panels
interface TwoPanelLayoutProps {
  leftPanel: ReactNode;
  rightPanel: ReactNode;
  leftMinWidth?: number;
  leftMaxWidth?: number;
  leftDefaultWidth?: number;
  rightMinWidth?: number;
  rightMaxWidth?: number;
  storageKey?: string;
}

export function TwoPanelLayout({
  leftPanel,
  rightPanel,
  leftMinWidth = 300,
  leftMaxWidth = 500,
  leftDefaultWidth = 380,
  rightMinWidth = 300,
  rightMaxWidth = 600,
  storageKey = 'two-panel-widths'
}: TwoPanelLayoutProps) {
  const [leftWidth, setLeftWidth] = useState(() => {
    if (typeof window !== 'undefined' && storageKey) {
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        try {
          const { left } = JSON.parse(saved);
          return left || leftDefaultWidth;
        } catch (e) {
          console.error('Failed to parse saved panel width:', e);
        }
      }
    }
    return leftDefaultWidth;
  });

  const [isResizing, setIsResizing] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const startXRef = useRef(0);
  const startWidthRef = useRef(0);

  useEffect(() => {
    if (storageKey && typeof window !== 'undefined') {
      localStorage.setItem(storageKey, JSON.stringify({ left: leftWidth }));
    }
  }, [leftWidth, storageKey]);

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
    startXRef.current = e.clientX;
    startWidthRef.current = leftWidth;
    
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing || !containerRef.current) return;

      const deltaX = e.clientX - startXRef.current;
      let newWidth = startWidthRef.current + deltaX;
      
      // Apply constraints
      newWidth = Math.max(leftMinWidth, Math.min(leftMaxWidth, newWidth));
      
      // Check if right panel has enough space
      const containerWidth = containerRef.current.offsetWidth;
      const maxLeftWidth = containerWidth - rightMinWidth - 1; // -1 for handle
      newWidth = Math.min(newWidth, maxLeftWidth);
      
      setLeftWidth(newWidth);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isResizing, leftMinWidth, leftMaxWidth, rightMinWidth]);

  return (
    <div ref={containerRef} className="flex h-full overflow-hidden">
      {/* Left Panel */}
      <div 
        className="h-full overflow-hidden"
        style={{ 
          width: `${leftWidth}px`,
          minWidth: `${leftMinWidth}px`,
          maxWidth: `${leftMaxWidth}px`
        }}
      >
        {leftPanel}
      </div>
      
      {/* Resize Handle */}
      <div
        className={`relative h-full bg-gray-200 cursor-col-resize hover:bg-gray-300 transition-colors ${isResizing ? 'bg-blue-400' : ''}`}
        style={{ width: '1px' }}
        onMouseDown={handleMouseDown}
      />
      
      {/* Right Panel (takes remaining space) */}
      <div className="flex-1 h-full overflow-hidden">
        {rightPanel}
      </div>
    </div>
  );
}